import React, { useEffect, useState } from "react";
import type { PopupRuntimeContext, PopupTree } from "./types";
import { PopupForm } from "./PopupForm";
import { sanitizeHtml } from "../utils/sanitize";

// Interpreta a árvore Craft.js serializada do popup e renderiza cada bloco com
// inline styles (o site publicado NÃO tem Tailwind). Espelha os ReadOnly do builder.

interface PopupNodeProps {
  nodeId: string;
  tree: PopupTree;
  ctx: PopupRuntimeContext;
  popupId: string;
  popupName: string;
  onConverted: () => void;
  onClose: () => void;
  forms: Record<string, unknown>;
  isMobile: boolean;
}

const resolvedName = (type: { resolvedName: string } | string): string =>
  typeof type === "string" ? type : type.resolvedName;

const margin = (m: Record<string, unknown> | undefined) => ({
  marginTop: m?.top as string | number,
  marginRight: m?.right as string | number,
  marginBottom: m?.bottom as string | number,
  marginLeft: m?.left as string | number,
});

const parseProportions = (proportion: string | undefined, count: number): number[] => {
  if (proportion && proportion.includes("-")) {
    const parts = proportion.split("-").map(Number).filter((n) => !Number.isNaN(n));
    if (parts.length === count) return parts;
  }
  return Array.from({ length: count }, () => 100 / count);
};

const childrenOf = (node: { nodes?: string[] }): string[] => node.nodes ?? [];

// Bloqueia href com protocolo perigoso (javascript:, data:) — o conteúdo é autoral do
// builder, mas o clique executa no browser do visitante. Aceita relativo/âncora e
// http(s)/tel/mailto.
const safeUrl = (url: unknown): string | undefined => {
  if (typeof url !== "string" || !url.trim()) return undefined;
  const trimmed = url.trim();
  if (trimmed.startsWith("/") || trimmed.startsWith("#")) return trimmed;
  try {
    const parsed = new URL(trimmed);
    return ["http:", "https:", "tel:", "mailto:"].includes(parsed.protocol) ? trimmed : undefined;
  } catch {
    return undefined;
  }
};

// Botão com hover (subcomponente para poder usar estado sem hook condicional).
const PopupButton = ({
  props,
  onActivate,
  onClose,
}: {
  props: Record<string, any>;
  onActivate?: () => void;
  onClose?: () => void;
}) => {
  const [hovered, setHovered] = useState(false);
  const style = props.style ?? {};
  const justify: Record<string, string> = { left: "flex-start", center: "center", right: "flex-end" };
  const safeHref = props.action === "url" ? safeUrl(props.actionUrl) : undefined;
  const isUrl = Boolean(safeHref);

  // Ação "Fechar popup" roda no clique do <button> (a ação "Abrir URL" vira <a>).
  const handleClick = () => {
    onActivate?.();
    if (props.action === "close") onClose?.();
  };

  const buttonStyle: React.CSSProperties = {
    backgroundColor: hovered ? style.hoverBgColor : style.bgColor,
    color: hovered ? style.hoverTextColor : style.textColor,
    fontSize: `${style.fontSize}px`,
    fontWeight: style.fontWeight,
    borderRadius: `${style.borderRadius}px`,
    border: style.border?.enabled
      ? `${style.border.width}px solid ${hovered ? style.border.hoverColor || style.border.color : style.border.color}`
      : "none",
    padding: `${style.padding?.vertical ?? 12}px ${style.padding?.horizontal ?? 24}px`,
    width: style.width === "full" ? "100%" : "auto",
    cursor: "pointer",
    textDecoration: "none",
    display: "inline-block",
    textAlign: "center",
    transition: "background-color 150ms, color 150ms",
  };

  const inner = isUrl ? (
    <a
      id={props.cssId || undefined}
      href={safeHref}
      target={props.openInNewTab ? "_blank" : "_self"}
      rel={props.openInNewTab ? "noopener noreferrer" : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onActivate}
      style={buttonStyle}
    >
      {props.text}
    </a>
  ) : (
    <button
      id={props.cssId || undefined}
      type="button"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleClick}
      style={buttonStyle}
    >
      {props.text}
    </button>
  );

  return (
    <div style={{ display: "flex", justifyContent: justify[style.alignment] || "center", ...margin(style.margin) }}>
      {inner}
    </div>
  );
};

const PopupCountdown = ({ props }: { props: Record<string, any> }) => {
  const style = props.style ?? {};
  const target = props.endDate || props.targetDate ? new Date(props.endDate || props.targetDate).getTime() : null;
  const [now, setNow] = useState<number>(() => Date.now());

  useEffect(() => {
    if (!target) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [target]);

  const diff = target ? Math.max(0, target - now) : 0;
  const units = {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    minutes: Math.floor((diff % 3600000) / 60000),
    seconds: Math.floor((diff % 60000) / 1000),
  };
  const labels: Record<string, string> = { days: "Dias", hours: "Horas", minutes: "Min", seconds: "Seg" };
  const display = (props.displayUnits ?? { days: true, hours: true, minutes: true, seconds: true }) as Record<string, boolean>;
  const active = (Object.keys(labels) as (keyof typeof units)[]).filter((u) => display[u]);
  const justify: Record<string, string> = { left: "flex-start", center: "center", right: "flex-end" };

  return (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, justifyContent: justify[style.alignment] || "flex-start", ...margin(style.margin) }}>
      {active.map((u) => (
        <div key={u} style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 48, padding: "6px 8px", backgroundColor: style.boxBgColor || "#0F172A", borderRadius: `${style.boxBorderRadius ?? 8}px` }}>
          <span style={{ fontSize: `${Math.min(style.numberSize ?? 28, 48)}px`, fontWeight: style.fontWeight ?? 700, color: style.numberColor || "#FFFFFF" }}>
            {String(units[u]).padStart(2, "0")}
          </span>
          <span style={{ fontSize: `${Math.min(style.labelSize ?? 12, 14)}px`, color: style.labelColor || "#CBD5E1" }}>
            {labels[u]}
          </span>
        </div>
      ))}
    </div>
  );
};

export const PopupNode = ({ nodeId, tree, ctx, popupId, popupName, onConverted, onClose, forms, isMobile }: PopupNodeProps) => {
  const node = tree[nodeId];
  if (!node || node.hidden) return null;

  const name = resolvedName(node.type);
  const props = (node.props ?? {}) as Record<string, any>;

  // Visibilidade por bloco (displayOn): 'mobile' só no mobile, 'desktop' só no desktop.
  // No builder isso é tratado pelo DisplayOnWrapper; no site publicado é aqui.
  if (props.displayOn === "mobile" && !isMobile) return null;
  if (props.displayOn === "desktop" && isMobile) return null;

  const renderChildren = (ids: string[]) =>
    ids.map((id) => (
      <PopupNode key={id} nodeId={id} tree={tree} ctx={ctx} popupId={popupId} popupName={popupName} onConverted={onConverted} onClose={onClose} forms={forms} isMobile={isMobile} />
    ));

  switch (name) {
    case "TextBlockWrapper": {
      const style = props.style ?? {};
      const Tag = (props.type || "p") as keyof JSX.IntrinsicElements;
      return (
        <Tag
          id={props.cssId || undefined}
          style={{
            fontFamily: props.fontFamily || undefined,
            fontSize: `${style.fontSize}px`,
            fontWeight: style.fontWeight,
            color: style.color,
            textAlign: style.textAlign || "left",
            lineHeight: style.lineHeight,
            letterSpacing: `${style.letterSpacing}px`,
            ...margin(style.margin),
          }}
        >
          {props.text}
        </Tag>
      );
    }

    case "RichTextBlockWrapper":
      // Texto rico: sanitizado (preserva formatação/links, remove script/handlers).
      return (
        <div
          id={props.cssId || undefined}
          style={{ fontFamily: props.fontFamily || undefined, ...margin(props.style?.margin) }}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(props.code || "") }}
        />
      );

    case "CustomHtmlBlockWrapper":
      // Sanitizado antes de renderizar no site publicado (visitante não-confiável).
      return props.code ? <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(props.code) }} /> : null;

    case "ImageBlockWrapper": {
      const style = props.style ?? {};
      if (!props.src) return null;
      const justify: Record<string, string> = { start: "flex-start", center: "center", end: "flex-end" };
      const img = (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          id={props.cssId || undefined}
          src={props.src}
          alt={props.alt || ""}
          style={{ width: style.width, height: style.height === "auto" ? "auto" : style.height, borderRadius: `${style.borderRadius}px`, objectFit: style.objectFit }}
        />
      );
      const clickHref = safeUrl(props.clickUrl);
      return (
        <div style={{ display: "flex", justifyContent: justify[style.alignment] || "flex-start", ...margin(style.margin) }}>
          {clickHref ? (
            <a href={clickHref} target={props.openInNewTab ? "_blank" : "_self"} rel={props.openInNewTab ? "noopener noreferrer" : undefined}>
              {img}
            </a>
          ) : (
            img
          )}
        </div>
      );
    }

    case "ButtonBlockWrapper":
      return <PopupButton props={props} onClose={onClose} />;

    case "VideoBlockWrapper": {
      const style = props.style ?? {};
      const container: React.CSSProperties = { width: style.width, aspectRatio: style.aspectRatio, borderRadius: `${style.borderRadius}px`, overflow: "hidden", ...margin(style.margin) };
      // Só a URL (src do iframe) é suportada no site publicado; embed HTML cru fica de fora.
      if (!props.url) return null;
      return (
        <div id={props.cssId || undefined} style={container}>
          <iframe src={props.url} title="Video" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen style={{ width: "100%", height: "100%", border: 0 }} />
        </div>
      );
    }

    case "CountdownBlockWrapper":
      return <PopupCountdown props={props} />;

    case "FormBlockWrapper":
      return (
        <PopupForm
          form={props.selectedFormId ? forms[props.selectedFormId] : undefined}
          teamId={props.selectedTeamId || undefined}
          ctx={ctx}
          popupId={popupId}
          popupName={popupName}
          onConverted={onConverted}
        />
      );

    case "ColumnsBlockWrapper": {
      const style = props.style ?? {};
      const count = props.count ?? 2;
      const widths = parseProportions(props.proportion, count);
      const vAlign: Record<string, string> = { top: "flex-start", center: "center", bottom: "flex-end" };
      const linked = node.linkedNodes ? Object.keys(node.linkedNodes).sort() : [];
      return (
        <div style={{ display: "flex", gap: `${style.gap ?? 16}px`, alignItems: vAlign[style.verticalAlignment] || "flex-start", ...margin(style.margin) }}>
          {widths.map((w, i) => {
            const colNodeId = linked[i] ? node.linkedNodes![linked[i]] : undefined;
            const colNode = colNodeId ? tree[colNodeId] : undefined;
            return (
              <div key={i} style={{ width: `${w}%` }}>
                {colNode ? renderChildren(childrenOf(colNode)) : null}
              </div>
            );
          })}
        </div>
      );
    }

    // Canvas / ROOT / ColumnDropZone e afins: apenas projetam filhos.
    default:
      return <>{renderChildren(childrenOf(node))}</>;
  }
};
